import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { authClient } from '@lib/betterAuth';
import { validatePassword } from '@lib/password';
import { BrandAuthShell } from '@components/brand/BrandAuthShell';
import { BrandCard } from '@components/brand/BrandCard';
import PasswordField from '@components/ui/PasswordField';
import PasswordStrengthMeter from '@components/ui/PasswordStrengthMeter';

type Status = 'idle' | 'submitting' | 'success' | 'error' | 'invalid';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<Status>(token ? 'idle' : 'invalid');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      newErrors.password = passwordValidation.message || t('passwordChange.passwordInvalid');
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = t('auth.reset.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !validate() || status === 'submitting') return;

    setStatus('submitting');
    setApiError('');

    try {
      await authClient.resetPassword({
        newPassword: password,
        token,
      });
      setStatus('success');
    } catch (err: unknown) {
      console.error('Password reset failed:', err);
      setStatus('error');
      setApiError(
        (err instanceof Error ? err.message : undefined) || t('auth.reset.failedMessage'),
      );
    }
  };

  if (status === 'invalid') {
    return (
      <BrandAuthShell>
        <BrandCard title={t('auth.reset.invalidLink')}>
          <div role="alert" className="fc-stack">
            <p className="fc-card-lead">{t('auth.reset.invalidLinkMessage')}</p>
            <Link to="/forgot-password">{t('auth.reset.reapply')}</Link>
          </div>
        </BrandCard>
      </BrandAuthShell>
    );
  }

  if (status === 'success') {
    return (
      <BrandAuthShell>
        <BrandCard title={t('auth.reset.success')}>
          <div role="status" className="fc-stack">
            <p className="fc-card-lead">{t('auth.reset.successMessage')}</p>
            <button type="button" className="fc-button" onClick={() => navigate('/login')}>
              {t('auth.reset.goToLogin')}
            </button>
          </div>
        </BrandCard>
      </BrandAuthShell>
    );
  }

  return (
    <BrandAuthShell>
      <BrandCard title={t('auth.reset.title')}>
        <form className="fc-stack" onSubmit={handleSubmit}>
          <p className="fc-card-lead">{t('auth.reset.subtitle')}</p>

          {apiError && (
            <div role="alert" className="fc-alert">
              {apiError}
            </div>
          )}

          <PasswordField
            name="password"
            label={t('auth.reset.newPassword')}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
            }}
            error={errors.password}
            autoComplete="new-password"
            required
          />

          {password && <PasswordStrengthMeter password={password} />}

          <PasswordField
            name="confirmPassword"
            label={t('auth.reset.confirmPassword')}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: '' }));
            }}
            error={errors.confirmPassword}
            autoComplete="new-password"
            required
          />

          <button type="submit" className="fc-button" disabled={status === 'submitting'}>
            {status === 'submitting' ? t('auth.reset.submitting') : t('auth.reset.submit')}
          </button>

          <Link to="/login">{t('auth.reset.backToLogin')}</Link>
        </form>
      </BrandCard>
    </BrandAuthShell>
  );
};

export default ResetPassword;
